import { createTheme } from '@mui/material/styles';
import { red } from '@mui/material/colors';
import React from 'react';
import {
  Link as RouterLink,
  LinkProps as RouterLinkProps,
} from 'react-router-dom';
import { LinkProps } from '@mui/material/Link';
// import { outlinedInputClasses } from '@mui/material/OutlinedInput';

const LinkBehavior = React.forwardRef<
  HTMLAnchorElement,
  Omit<RouterLinkProps, 'to'> & { href: RouterLinkProps['to'] }
>((props, ref) => {
  const { href, ...other } = props;
  // Map href (MUI) -> to (react-router)
  return (
    <RouterLink data-testid="custom-link" ref={ref} to={href} {...other} />
  );
});

// A custom theme for this app
const jbank_theme = createTheme({
  props: {
    // Name of the component ⚛️
    MuiButtonBase: {
      // The properties to apply
      disableRipple: true, // No more ripple, on the whole application 💣!
    },
  },
  typography: {
    fontFamily: 'Inter,sans-serif',
    fontSize: 14,
  },
  transitions: {
    /* duration: {
      shortest: 5,
      shorter: 5,
      short: 5,
      // most basic recommended timing
      standard: 5,
      // this is to be used in complex animations
      complex: 5,
      // recommended when something is entering screen
      enteringScreen: 5,
      // recommended when something is leaving screen
      leavingScreen: 5,
    }, */
    // create: () => 'none',
  },
  palette: {
    mode: 'light',
    background: {
      default: '#F6F7F9',
      paper: '#FFFFFF',
    },
    primary: {
      main: '#2F62AD',
    },
    secondary: {
      main: '#000000',
    },
    error: {
      main: red.A400,
    },
    divider: '#bebeb633',
    borderGray: {
      main: '#E8E9EE',
      light: '#f6f6f5',
      dark: '#D8DBE3',
    },
    navLink: '#6a7186',
    description: {
      main: '#7D8499',
      light: '#19304866',
    },
  },

  shape: {
    borderRadius: 18,
  },

  components: {
    MuiInput: {
      styleOverrides: {
        root: {
          '&:before': {
            borderBottom: '2px solid #D8DBE3',
          },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
            {
              display: 'none',
            },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          // '&:hover:not(.Mui-disabled, .Mui-error):before': {
          //   borderBottom: '2px solid var(--TextField-brandBorderHoverColor)',
          // },
          // '&.Mui-focused:after': {
          //   borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
          // },
        },
      },
    },
    // MuiFilledInput: {
    //   styleOverrides: {
    //     root: {
    //       '&:before, &:after': {
    //         borderBottom: '2px solid var(--TextField-brandBorderColor)',
    //       },
    //       '&:hover:not(.Mui-disabled, .Mui-error):before': {
    //         borderBottom: '2px solid var(--TextField-brandBorderHoverColor)',
    //       },
    //       '&.Mui-focused:after': {
    //         borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
    //       },
    //     },
    //   },
    // },
    MuiOutlinedInput: {
      styleOverrides: {
        notchedOutline: {
          borderColor: '#D8DBE3',
          // borderRadius: '18px',
        },
        root: {
          '&:before': {
            borderBottom: '2px solid #D8DBE3',
          },
          '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button':
            {
              display: 'none',
            },
          '& input[type=number]': {
            MozAppearance: 'textfield',
          },
          [`&:hover .MuiOutlinedInput-notchedOutline`]: {
            borderColor: '#9a9a9a',
            transition: 'border-color 0.2s ease-in-out',
          },
          [`&.Mui-focused .MuiOutlinedInput-notchedOutline`]: {
            borderColor: '#2F62AD',
            transition: 'border-color 0.2s ease-in-out',
          },
          // '&:hover:not(.Mui-disabled, .Mui-error):before': {
          //   borderBottom: '2px solid var(--TextField-brandBorderHoverColor)',
          // },
          // '&.Mui-focused:after': {
          //   borderBottom: '2px solid var(--TextField-brandBorderFocusedColor)',
          // },
        },
        // root: {
        //   [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
        //     borderColor: 'var(--TextField-brandBorderHoverColor)',
        //   },
        //   [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
        //     borderColor: 'var(--TextField-brandBorderFocusedColor)',
        //   },
        // },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          calcHeading: 'h1',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: '30px',
          margin: '16px',
          width: 'calc(100% - 16px)',
          maxHeight: 'calc(100% - 16px)',
        },
      },
    },
    MuiLink: {
      defaultProps: {
        component: LinkBehavior,
      } as LinkProps,
    },

    MuiButton: {
      variants: [
        {
          props: { variant: 'default' },
          style: {
            backgroundColor: '#2F62AD',
            color: '#FFFFFF',
            borderRadius: '18px',
            padding: '10px 32px',
            fontSize: '14px',
            fontWeight: '600',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: 'rgba(47, 98, 173, 0.20)',
            },
            '&.Mui-disabled': {
              backgroundColor: '#0000001f',
            },
          },
        },

        {
          props: { variant: 'contained' },
          style: {
            backgroundColor: '#FFDF11',
            color: '#000000',
            borderRadius: '18px',
            padding: '10px 32px',
            fontSize: '14px',
            fontWeight: '600',
            textTransform: 'none',
            '&:hover': {
              backgroundColor: '#f5d400',
            },
            '&.Mui-disabled': {
              backgroundColor: '#f0f0f0',
              color: '#999',
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          borderRadius: '18px',
          // padding: '10px 32px',
          fontSize: '14px',
          fontWeight: '600',
          // textTransform: 'none',
        },
      },
    },

    MuiButtonBase: {
      // styleOverrides: {
      //   root: {
      //     '&:hover': {
      //       backgroundColor: '#7c7c7b !important',
      //     },
      //   },
      // },
      defaultProps: {
        LinkComponent: LinkBehavior,
      },
    },

    MuiCssBaseline: {
      styleOverrides: {
        '*, *::before, *::after': {
          // transition: 'none !important',
          // animation: 'none !important',
        },
      },
    },

    // Name of the component
    MuiBottomNavigationAction: {
      styleOverrides: {
        // Name of the slot

        label: {
          fontSize: '9px',
          color: '#AAADB2',
          '&.Mui-selected': {
            fontSize: '9px',
            color: 'rgba(47, 98, 173, 0.20)',
          },
        },
      },
    },

    MuiSnackbar: {
      styleOverrides: {
        root: {
          // bottom: '70px',
          // '@media (min-width: 600px)': {
          //   bottom: '64px',
          // },
        },
      },
    },

    MuiAccordion: {
      defaultProps: {
        disableGutters: true,
      },
      styleOverrides: {
        root: {
          boxShadow: '0 2px 12px #edeff0',
          borderRadius: '16px',
          paddingTop: '16px',
          paddingRight: '24px',
          paddingLeft: '20px',
          paddingBottom: '16px',
          marginBottom: '20px',
          '@media (max-width: 899px)': {
            padding: '8px 0',
            marginBottom: '15px',
          },
          '&:before': {
            display: 'none',
          },
          ul: {
            paddingLeft: '20px',
          },
          ol: {
            paddingLeft: '20px',
          },
          '&:first-of-type': {
            borderTopRightRadius: '16px',
            borderTopLeftRadius: '16px',
            borderBottomRightRadius: '16px',
            borderBottomLeftRadius: '16px',
          },
          '&:last-of-type': {
            borderTopRightRadius: '16px',
            borderTopLeftRadius: '16px',
            borderBottomRightRadius: '16px',
            borderBottomLeftRadius: '16px',
            marginBottom: 0,
          },
        },
      },
    },
  },
});

jbank_theme.typography.calcHeading = {
  display: 'flex',
  justifyContent: 'center',
  fontSize: '32px',
  fontWeight: '500',
  lineHeight: '1.2',
  margin: '32px 0',
  textAlign: 'center',
  [jbank_theme.breakpoints.down('md')]: {
    fontSize: '22px',
  },
};

export const jbankTheme = createTheme({
  ...jbank_theme,
  palette: {
    ...jbank_theme.palette,
    background: {
      ...jbank_theme.palette.background,
      default: '#FFFFFF', // Измененный параметр
    },
  },
});

export default jbank_theme;
